import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

// A page with unsaved work registers a guard; navigation entry points
// (sidebar, header buttons) route through guardedNavigate, which asks the
// user before leaving. React Router 6 with plain <Routes> has no usable
// blocker, so this context stands in for it.
//
// Only one guard is active at a time: the page that mounts last wins, and a
// page unregisters when it unmounts.

const UnsavedWorkContext = createContext({
  registerGuard: () => () => {},
  guardedNavigate: (go) => go(),
});

const CONTROL_RADIUS = "6px";

export function UnsavedWorkProvider({ children }) {
  const guardRef = useRef(null);
  // The navigation waiting on the user's answer, stored as a function.
  const [pendingGo, setPendingGo] = useState(null);

  const registerGuard = useCallback((guard) => {
    guardRef.current = guard;
    return () => {
      if (guardRef.current === guard) guardRef.current = null;
    };
  }, []);

  const guardedNavigate = useCallback((go) => {
    if (guardRef.current?.isDirty()) {
      setPendingGo(() => go);
      return;
    }
    go();
  }, []);

  const stay = useCallback(() => setPendingGo(null), []);

  const leave = useCallback(() => {
    const go = pendingGo;
    setPendingGo(null);
    guardRef.current?.discard();
    if (go) go();
  }, [pendingGo]);

  const value = useMemo(
    () => ({ registerGuard, guardedNavigate }),
    [registerGuard, guardedNavigate]
  );

  return (
    <UnsavedWorkContext.Provider value={value}>
      {children}
      <Dialog
        open={Boolean(pendingGo)}
        onClose={stay}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: "1.125rem",
            lineHeight: 1.3,
            letterSpacing: 0,
          }}
        >
          Unsaved receiving
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have an unsaved receiving. If you leave now it will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={stay}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Stay
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={leave}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: CONTROL_RADIUS,
              boxShadow: "none",
            }}
          >
            Leave and lose changes
          </Button>
        </DialogActions>
      </Dialog>
    </UnsavedWorkContext.Provider>
  );
}

export const useUnsavedWork = () => useContext(UnsavedWorkContext);
